/**
 * 事前連絡 API（/api/advance-notices, /api/advance-notice-vessels）。担当: G2
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type RegistrationType = '新規登録' | '変更' | '中止';
export type ChangeType = '変更無し' | '変更' | '追加' | '削除';

/**
 * 変更区分の画面表示名。実施船の「削除」（行を残して取りやめ）は「中止」と表示する（kintone 検証指摘 R08.09.11）。
 * DB・API の値は「削除」のまま（帳票・海域図・送信状況判定の除外条件と互換）。
 */
export const changeTypeLabel = (t: ChangeType | string | null | undefined): string => (t === '削除' ? '中止' : (t ?? ''));

export interface NoticeListItem {
  noticeId: number;
  noticeNo: string;
  registrationType: RegistrationType;
  approvalStatus: '承認' | '承認待ち';
  reportDate: string;
  reporterName: string;
  businessOperatorId: number;
  businessOperatorName: string | null;
  noticeVesselId: number;
  vesselId: number | null;
  vesselName: string | null;
  unlistedVesselName: string | null;
  registrationNo: string | null;
  phone: string | null;
  crewCount: number;
  portName: string;
  departureDate: string;
  arrivalDate: string;
  departureAt: string;
  arrivalAt: string;
  zoneNumbers: string;
  catchSurveyPlanned: boolean;
  catchSalePlanned: boolean;
  changeType: ChangeType;
  fixedReportCount: number;
  voyageReportId: number | null;
}

export interface NoticeListFilter {
  businessOperatorId?: number | '';
  vesselName?: string;
  registrationNo?: string;
  portName?: string;
  registrationType?: RegistrationType | '';
  departureFrom?: string;
  departureTo?: string;
  arrivalFrom?: string;
  arrivalTo?: string;
  page?: number;
}

export interface NoticeVesselDto {
  id: number;
  rowKey: string;
  sortOrder: number;
  businessOperatorId: number | null;
  businessOperatorName: string | null;
  vesselId: number | null;
  vesselName: string | null;
  registrationNo: string | null;
  unlistedVesselName: string | null;
  unlistedReason: string | null;
  phone: string | null;
  crewCount: number;
  portName: string;
  departureDate: string;
  departureHour: number;
  departureMinute: number;
  arrivalDate: string;
  arrivalHour: number;
  arrivalMinute: number;
  zoneNumbers: string;
  catchSurveyPlanned: boolean;
  catchSalePlanned: boolean;
  note: string | null;
  changeType: ChangeType;
  vesselChangeReason: string | null;
  phoneChangeReason: string | null;
  crewChangeReason: string | null;
  portChangeReason: string | null;
  departureChangeReason: string | null;
  arrivalChangeReason: string | null;
  zoneChangeReason: string | null;
}

export interface NoticeHistoryDto {
  id: number;
  appliedAt: string;
  registrationType: RegistrationType;
  reporterName: string;
  changeReason: string | null;
  beforeSnapshot: Partial<NoticeVesselDto>[];
}

export interface NoticeDetail {
  id: number;
  fiscalYear: number;
  businessOperatorId: number;
  businessOperatorName: string;
  noticeNo: string;
  reporterName: string;
  reportDate: string;
  registrationType: RegistrationType;
  changeReason: string | null;
  approvalStatus: '承認' | '承認待ち';
  changeAppliedOn: string | null;
  changeApprovedOn: string | null;
  fundComment: string | null;
  updatedAt: string;
  vessels: NoticeVesselDto[];
  histories: NoticeHistoryDto[];
}

export interface NoticeVesselInput {
  rowKey?: string | null;
  vesselId: number | null;
  businessOperatorId: number | null;
  unlistedVesselName: string | null;
  unlistedReason: string | null;
  phone: string | null;
  crewCount: number | null;
  portName: string;
  departureDate: string;
  departureHour: number | null;
  departureMinute: number | null;
  arrivalDate: string;
  arrivalHour: number | null;
  arrivalMinute: number | null;
  zoneNumbers: string;
  catchSurveyPlanned: boolean;
  catchSalePlanned: boolean;
  note: string | null;
  changeType: ChangeType | null;
  vesselChangeReason: string | null;
  phoneChangeReason: string | null;
  crewChangeReason: string | null;
  portChangeReason: string | null;
  departureChangeReason: string | null;
  arrivalChangeReason: string | null;
  zoneChangeReason: string | null;
}

export interface NoticeInput {
  fiscalYear: number;
  businessOperatorId: number | null;
  reporterName: string;
  reportDate: string;
  registrationType?: RegistrationType;
  changeReason: string | null;
  vessels: NoticeVesselInput[];
  confirmations: Record<string, boolean>;
  id?: number;
}

export interface Issue {
  field?: string;
  rowIndex?: number;
  path?: string;
  message: string;
}

export interface Confirmation {
  key: 'SHORT_WORK_DEPARTURE' | 'SHORT_WORK_ARRIVAL' | 'DEPARTED_ALREADY';
  rowIndex: number;
  message: string;
}

export interface ValidateResult {
  errors: Issue[];
  approval: '承認' | '否認' | null;
  denialReasons: { rowIndex: number | null; message: string }[];
  confirmations: Confirmation[];
  missingConfirmations: string[];
  changeTypes: ChangeType[];
}

export interface NoticeVesselContext {
  noticeVessel: NoticeVesselDto;
  notice: { id: number; noticeNo: string; registrationType: RegistrationType; fiscalYear: number };
  vessel: { id: number; name: string; nameKana: string; registrationNo: string };
  businessOperator: { id: number; name: string };
  period: { from: string; to: string };
  existingFixedReportDates: string[];
  existingFixedReports: { id: number; reportDate: string }[];
  voyageReportId: number | null;
}

export const noticeKeys = {
  all: ['advance-notices'] as const,
  list: (year: number, f: NoticeListFilter) => [...noticeKeys.all, 'list', year, f] as const,
  detail: (id: number) => [...noticeKeys.all, 'detail', id] as const,
  context: (rowId: number) => [...noticeKeys.all, 'context', rowId] as const,
};

export const PAGE_SIZE = 50;

export function useNotices(year: number, f: NoticeListFilter) {
  return useQuery({
    queryKey: noticeKeys.list(year, f),
    queryFn: ({ signal }) =>
      api.get<{ items: NoticeListItem[]; total: number }>('/advance-notices', { fiscalYear: year, pageSize: PAGE_SIZE, ...f }, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useNotice(id: number | null) {
  return useQuery({
    queryKey: noticeKeys.detail(id ?? 0),
    queryFn: ({ signal }) => api.get<NoticeDetail>(`/advance-notices/${id}`, undefined, { signal }),
    enabled: id != null,
  });
}

export function useNoticeVesselContext(rowId: number | null) {
  return useQuery({
    queryKey: noticeKeys.context(rowId ?? 0),
    queryFn: ({ signal }) => api.get<NoticeVesselContext>(`/advance-notice-vessels/${rowId}/context`, undefined, { signal }),
    enabled: rowId != null,
  });
}

export const validateNotice = (input: NoticeInput) => api.post<ValidateResult>('/advance-notices/validate', input);

export function useSaveNotice(id: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NoticeInput) =>
      id ? api.put<{ id: number; noticeNo: string }>(`/advance-notices/${id}`, input) : api.post<{ id: number; noticeNo: string }>('/advance-notices', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: noticeKeys.all }),
  });
}

export function useFundReview(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { approvalStatus: string; changeApprovedOn: string | null; fundComment: string | null }) =>
      api.put<NoticeDetail>(`/advance-notices/${id}/fund-review`, input),
    onSuccess: (d) => {
      qc.setQueryData(noticeKeys.detail(id), d);
      return qc.invalidateQueries({ queryKey: noticeKeys.all });
    },
  });
}

export function useDeleteNotice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/advance-notices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: noticeKeys.all }),
  });
}

/** 時:分 の表示 */
export const hm = (h: number | null | undefined, m: number | null | undefined) =>
  h == null ? '' : `${String(h).padStart(2, '0')}:${String(m ?? 0).padStart(2, '0')}`;
